import * as THREE from 'three';

const TRACE_SNAP_DIST = 20; // world units — mouse bu mesafe içinde olursa trace aktif

function polyArcLen(pts) {
    let len = 0;
    for (let i = 0; i < pts.length - 1; i++) len += pts[i].distanceTo(pts[i + 1]);
    return len;
}

function ptAtArcT(pts, t) {
    const total = polyArcLen(pts);
    if (total < 1e-9) return pts[0].clone();
    const target = total * Math.max(0, Math.min(1, t));
    let acc = 0;
    for (let i = 0; i < pts.length - 1; i++) {
        const seg = pts[i].distanceTo(pts[i + 1]);
        if (acc + seg >= target - 1e-9) {
            return pts[i].clone().lerp(pts[i + 1], Math.min(1, (target - acc) / Math.max(1e-9, seg)));
        }
        acc += seg;
    }
    return pts[pts.length - 1].clone();
}

function tangentAtArcT(pts, t) {
    const total = polyArcLen(pts);
    if (total < 1e-9) return new THREE.Vector3(1, 0, 0);
    const target = total * Math.max(0, Math.min(1, t));
    let acc = 0;
    for (let i = 0; i < pts.length - 1; i++) {
        const seg = pts[i].distanceTo(pts[i + 1]);
        if (acc + seg >= target - 1e-9) {
            return new THREE.Vector3().subVectors(pts[i + 1], pts[i]).normalize();
        }
        acc += seg;
    }
    const n = pts.length;
    return new THREE.Vector3().subVectors(pts[n - 1], pts[n - 2]).normalize();
}

/**
 * Bir dünya noktasını polyline üzerine projekte eder. Arc-T [0,1] döndürür.
 */
export function projectOntoWire(pts, worldPt) {
    const total = polyArcLen(pts);
    if (total < 1e-9) return 0;
    let bestT = 0, bestD = Infinity, acc = 0;
    for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i], b = pts[i + 1];
        const ab = new THREE.Vector3().subVectors(b, a);
        const len = ab.length();
        if (len < 1e-9) { acc += len; continue; }
        const t = Math.max(0, Math.min(1, new THREE.Vector3().subVectors(worldPt, a).dot(ab) / (len * len)));
        const d = a.clone().lerp(b, t).distanceTo(worldPt);
        if (d < bestD) { bestD = d; bestT = (acc + t * len) / total; }
        acc += len;
    }
    return bestT;
}

/**
 * Bir noktanın polyline'ın hangi tarafında olduğunu belirler. +1 veya -1 döndürür.
 */
export function getSideOfWire(pts, point) {
    let minDist = Infinity, side = 1;
    for (let i = 0; i < pts.length - 1; i++) {
        const a = pts[i], b = pts[i + 1];
        const ab = new THREE.Vector3().subVectors(b, a);
        const len = ab.length();
        if (len < 1e-9) continue;
        const t = Math.max(0, Math.min(1, new THREE.Vector3().subVectors(point, a).dot(ab) / (len * len)));
        const proj = a.clone().lerp(b, t);
        const d = proj.distanceTo(point);
        if (d < minDist) {
            minDist = d;
            const abN = ab.clone().normalize();
            const tp = new THREE.Vector3().subVectors(point, proj);
            side = (abN.x * tp.y - abN.y * tp.x) >= 0 ? 1 : -1;
        }
    }
    return side;
}

/**
 * Scene'deki en yakın mevcut wire Line'ı bulur.
 * { wire, positions, projT } veya null döndürür.
 */
export function findGuideWireAtPoint(scene, worldPt, excludeName) {
    let best = null, bestDist = TRACE_SNAP_DIST;
    scene.traverse(obj => {
        if (!obj.isLine) return;
        if (obj.userData?.isTransient || obj.userData?.isBundleSelection) return;
        if (obj.userData?.isBundleRep) return;
        if (excludeName && obj.name === excludeName) return;
        if (!obj.geometry?.attributes?.position) return;
        const pa = obj.geometry.attributes.position;
        if (pa.count < 2) return;

        const positions = [];
        for (let i = 0; i < pa.count; i++) {
            positions.push(new THREE.Vector3(pa.array[i * 3], pa.array[i * 3 + 1], 0));
        }

        const total = polyArcLen(positions);
        let minD = Infinity, minT = 0, acc = 0;
        for (let i = 0; i < positions.length - 1; i++) {
            const a = positions[i], b = positions[i + 1];
            const ab = new THREE.Vector3().subVectors(b, a);
            const len = ab.length();
            if (len < 1e-9) { acc += len; continue; }
            const t = Math.max(0, Math.min(1, new THREE.Vector3().subVectors(worldPt, a).dot(ab) / (len * len)));
            const d = a.clone().lerp(b, t).distanceTo(worldPt);
            if (d < minD) { minD = d; minT = total > 0 ? (acc + t * len) / total : 0; }
            acc += len;
        }
        if (minD < bestDist) { bestDist = minD; best = { wire: obj, positions, projT: minT }; }
    });
    return best;
}

// ── Yardımcı fonksiyonlar ────────────────────────────────────────────────────

/**
 * 2D ışın kesişimi: p1 + t*d1 = p2 + s*d2
 * Paralel ise null döndürür.
 */
function rayIntersect2D(p1, d1, p2, d2) {
    const denom = d1.x * d2.y - d1.y * d2.x;
    if (Math.abs(denom) < 1e-10) return null;
    const dx = p2.x - p1.x, dy = p2.y - p1.y;
    const t = (dx * d2.y - dy * d2.x) / denom;
    return new THREE.Vector3(p1.x + t * d1.x, p1.y + t * d1.y, 0);
}

/**
 * Bir noktayı teğet yönüne dik olarak `side * dist` kadar öteleyerek döndürür.
 */
function perpOffset(p, tang, side, dist) {
    const perp = new THREE.Vector3(-tang.y, tang.x, 0);
    return new THREE.Vector3(
        p.x + perp.x * side * dist,
        p.y + perp.y * side * dist,
        0
    );
}

// ────────────────────────────────────────────────────────────────────────────

/**
 * Klavuz polyline üzerinde fromT'den toT'ye kadar trigonometrik olarak doğru
 * paralel-ofset yolu hesaplar.
 *
 * Her köşede iki bitişik segment'in ofset doğrularının kesişim noktası (miter)
 * kullanılır; bu sayede açılı wire'larda da tam paralel 5 birimlik mesafe korunur.
 * Çok keskin açılarda (miter mesafesi 4×offsetDist üzerinde) kesişim yerine
 * her iki segment'in bağlantı noktalarının ortası kullanılır.
 */
export function computeOffsetTracePath(pts, fromT, toT, offsetDist, side) {
    if (pts.length < 2) return [];
    const total = polyArcLen(pts);
    if (total < 1e-9) return [];

    const fromArc = fromT * total;
    const toArc   = toT   * total;
    const forward = toArc >= fromArc;

    // Polyline köşelerinin arc değerleri
    const vertexArcs = [0];
    let acc = 0;
    for (let i = 0; i < pts.length - 1; i++) {
        acc += pts[i].distanceTo(pts[i + 1]);
        vertexArcs.push(acc);
    }

    // from → araya giren köşeler → to  (arc değerleri listesi)
    const arcVals = [fromArc];
    if (forward) {
        for (const va of vertexArcs) {
            if (va > fromArc + 1e-6 && va < toArc - 1e-6) arcVals.push(va);
        }
    } else {
        for (let i = vertexArcs.length - 1; i >= 0; i--) {
            if (vertexArcs[i] < fromArc - 1e-6 && vertexArcs[i] > toArc + 1e-6) arcVals.push(vertexArcs[i]);
        }
    }
    arcVals.push(toArc);

    // Arc değerlerini dünya noktalarına çevir
    const subPath = arcVals.map(arc => ptAtArcT(pts, arc / total));
    if (subPath.length === 1) {
        const tang = tangentAtArcT(pts, arcVals[0] / total);
        return [perpOffset(subPath[0], tang, side, offsetDist)];
    }

    // Her vertex için doğru ofset noktasını hesapla
    const result = [];
    const MITER_LIMIT = 4 * offsetDist; // aşırı keskin açılarda miter kırpılır

    for (let i = 0; i < subPath.length; i++) {
        const p = subPath[i];

        if (i === 0) {
            // Başlangıç: ilk segment'e dik ofset
            const tang = new THREE.Vector3().subVectors(subPath[1], subPath[0]).normalize();
            result.push(perpOffset(p, tang, side, offsetDist));

        } else if (i === subPath.length - 1) {
            // Bitiş: son segment'e dik ofset
            const tang = new THREE.Vector3().subVectors(subPath[i], subPath[i - 1]).normalize();
            result.push(perpOffset(p, tang, side, offsetDist));

        } else {
            // İç köşe: iki bitişik segment'in ofset doğrularının kesişimi (miter)
            const prevDir = new THREE.Vector3().subVectors(subPath[i],     subPath[i - 1]).normalize();
            const nextDir = new THREE.Vector3().subVectors(subPath[i + 1], subPath[i]    ).normalize();

            // Her iki segment'in ofset başlangıç noktaları
            const o1 = perpOffset(p, prevDir, side, offsetDist); // p → prev yönünden ofset
            const o2 = perpOffset(p, nextDir, side, offsetDist); // p → next yönünden ofset

            const miter = rayIntersect2D(o1, prevDir, o2, nextDir);

            if (miter && miter.distanceTo(p) <= MITER_LIMIT) {
                result.push(miter);
            } else {
                // Paralel veya çok keskin açı → iki ofset noktasının ortası
                result.push(new THREE.Vector3((o1.x + o2.x) / 2, (o1.y + o2.y) / 2, 0));
            }
        }
    }

    return result;
}
